import { API_URL } from 'constants/api';
import { CITIES_KEY } from 'constants/local-storage';
import {
  FunctionComponent,
  PropsWithChildren,
  createContext,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { ApiResponse } from 'types/api';
import { useRevalidate } from 'hooks/useRevalidate';
import { CITY, CITY_QUERY_PARAM, DEFAULT_CITY_ID } from '../constants/city';
import { ROUTES } from '../constants/routes';
import { useNotifications } from './notifications';
import { wrappedFetch } from 'utils/api';
import {
  useLocation,
  useNavigate as useNavigateDefault,
  useSearchParams,
} from 'react-router-dom';
import { buildSearchWithCityId, parseCityId } from 'utils/city-url';

export interface City {
  id: number;
  cityFullName: string;
  citySystemName: string;
  cityName: string;
  cityLat: string;
  cityLng: string;
  cityLabel: string;
  mapItemsShow: number;
  pageSize: number;
  siteMainColor: string;
  siteSecondColor: string;
  phone: string;
  mail: string;
  only_registered: number;
  logo_image_url: string;
  logo_image: string;
  problem_submission_message: string | null;
  welcome_message: string | null;
}

const CitiesContext = createContext<{
  cities: City[];
  selectCity: (cityId: number) => void;
  selectedCity: City | null;
  isCityResolved: boolean;
}>({} as any);

export const useCities = () => useContext(CitiesContext);

interface CitiesProviderProps extends PropsWithChildren {}

const CitiesProvider: FunctionComponent<CitiesProviderProps> = ({
  children,
}) => {
  const [cities, setCities] = useState<City[]>([]);
  const [selectedCity, setSelectedCity] = useState<City | null>(null);
  const [isCityResolved, setIsCityResolved] = useState(false);
  const { data, mutate } = useRevalidate<City[]>(CITIES_KEY, 3600);
  const { notify } = useNotifications();
  const navigate = useNavigateDefault();
  const [searchParams, setSearchParams] = useSearchParams();
  const location = useLocation();
  const locationSearchRef = useRef(location.search);
  const lastWelcomeCityId = useRef<number | null>(null);
  locationSearchRef.current = location.search;

  const cityIdFromUrl = useMemo(
    () => parseCityId(searchParams.get(CITY_QUERY_PARAM)),
    [searchParams],
  );

  const getStoredCityId = () => parseCityId(localStorage.getItem(CITY));

  const persistCityId = (cityId: number) => {
    localStorage.setItem(CITY, cityId.toString());
  };

  useEffect(() => {
    if (cities.length === 0) {
      return;
    }

    const defaultCity =
      cities.find((city) => city.id === DEFAULT_CITY_ID) ?? cities[0] ?? null;
    const cityFromUrl =
      cityIdFromUrl == null
        ? null
        : cities.find((city) => city.id === cityIdFromUrl) ?? null;
    const storedCityId = getStoredCityId();
    const cityFromStorage =
      storedCityId == null
        ? null
        : cities.find((city) => city.id === storedCityId) ?? null;
    const nextCity = cityFromUrl ?? cityFromStorage ?? defaultCity;

    setSelectedCity((prev) => (prev?.id === nextCity?.id ? prev : nextCity));
    setIsCityResolved(true);

    if (nextCity) {
      persistCityId(nextCity.id);
    }

    const nextSearch = buildSearchWithCityId(
      locationSearchRef.current,
      nextCity?.id ?? null,
    );

    if (nextSearch !== locationSearchRef.current) {
      setSearchParams(nextSearch, { replace: true });
    }
  }, [cities, cityIdFromUrl, setSearchParams]);

  const selectCity = (cityId: number) => {
    const nextCity = cities.find((city) => city.id === cityId) ?? null;

    if (!nextCity) {
      return;
    }

    persistCityId(nextCity.id);
    setSelectedCity((prev) => (prev?.id === nextCity.id ? prev : nextCity));
    setIsCityResolved(true);

    const isMessagesPage = location.pathname === ROUTES.messages;
    const target = isMessagesPage ? location.pathname : ROUTES.home;
    const nextSearch = buildSearchWithCityId(
      isMessagesPage ? location.search : '',
      nextCity.id,
    );

    navigate(`${target}${nextSearch}`, {
      replace: isMessagesPage,
    });
  };

  useEffect(() => {
    if (!selectedCity?.welcome_message) {
      return;
    }

    if (lastWelcomeCityId.current === selectedCity.id) {
      return;
    }

    lastWelcomeCityId.current = selectedCity.id;
    notify({ text: selectedCity.welcome_message, type: 'information' });
  }, [notify, selectedCity]);

  useEffect(() => {
    if (data) {
      setCities(data);
    } else {
      wrappedFetch(`${API_URL}city/list`)
        .then((res: ApiResponse<any[]>) => {
          if ('data' in res) {
            const newData = res.data.map(({ polygon, ...rest }) => ({
              ...rest,
            }));
            setCities(newData);
            mutate(newData);
          }
        })
        .catch(() => {});
    }
  }, [data, mutate]);

  return (
    <CitiesContext.Provider
      value={{ cities, selectCity, selectedCity, isCityResolved }}
    >
      {children}
    </CitiesContext.Provider>
  );
};

export default CitiesProvider;
